import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { USER_ROLES } from '@/types/user';
import type { UserRole } from '@/types/user';

/**
 * The user table's state, held in the URL and nowhere else.
 *
 * Same rule as the flight board: THE URL IS THE STATE. It is read on render and
 * written on an action, never mirrored into React state — so a refresh keeps the
 * search, a link carries it, and Back works without a line of code for it.
 *
 * Unlike the board, none of this reaches the server: `/user/all` takes no filter, so
 * every one of these narrows an array already in the browser. They are in the URL
 * anyway, because "the blocked accounts, sorted by company" is a thing one
 * administrator sends another.
 */

const KEYS = {
  query: 'q',
  role: 'role',
  state: 'state',
  access: 'access',
  sort: 'sort',
  dir: 'dir',
  /** The panel: a user id, or `new` for the create form. */
  id: 'id',
} as const;

/** The create form, in the same key as the row being edited — never both at once. */
const CREATE = 'new';

// `company` was a sort field while the table had the column; a saved link that
// still says `sort=company` now falls back to the default rather than erroring.
export const USER_SORT_FIELDS = ['username', 'name', 'role', 'created'] as const;
export type UserSortField = (typeof USER_SORT_FIELDS)[number];

/** Alphabetical by login: the list is read to find a person, not to rank them. */
const DEFAULT_SORT: UserSortField = 'username';

/** Whether the account may sign in, as a filter. */
export const USER_STATES = ['active', 'blocked'] as const;
export type UserState = (typeof USER_STATES)[number];

/**
 * Which flights the account sees, as a filter — the access that APPLIES, so an
 * administrator is always `all` whatever is stored for them.
 */
export const USER_ACCESS_FILTERS = ['all', 'restricted'] as const;
export type UserAccessFilter = (typeof USER_ACCESS_FILTERS)[number];

/**
 * Which panel is open.
 *
 * Three states in one union rather than an id plus an `isCreating` boolean, because
 * two of the four combinations that pair would allow — creating while editing, and
 * neither-yet-both — are not states this screen has.
 */
export type Selection =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; id: number };

export interface UserParams {
  /** Matched against login, name, email and phone — see `matches` in UsersPage. */
  query: string;
  role: UserRole | '';
  state: UserState | '';
  access: UserAccessFilter | '';
  sort: UserSortField;
  dir: 'ascending' | 'descending';
  selection: Selection;
}

const isOneOf = <T extends string>(options: readonly T[], value: string | null): value is T =>
  value !== null && (options as readonly string[]).includes(value);

export const useUserParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<UserParams>(() => {
    const role = searchParams.get(KEYS.role);
    const state = searchParams.get(KEYS.state);
    const access = searchParams.get(KEYS.access);
    const sort = searchParams.get(KEYS.sort);
    const selected = searchParams.get(KEYS.id);
    const id = Number(selected);

    return {
      query: searchParams.get(KEYS.query)?.trim() ?? '',
      role: isOneOf(USER_ROLES, role) ? role : '',
      state: isOneOf(USER_STATES, state) ? state : '',
      access: isOneOf(USER_ACCESS_FILTERS, access) ? access : '',
      sort: isOneOf(USER_SORT_FIELDS, sort) ? sort : DEFAULT_SORT,
      dir: searchParams.get(KEYS.dir) === 'descending' ? 'descending' : 'ascending',
      selection:
        selected === CREATE
          ? { mode: 'create' }
          : Number.isInteger(id) && id > 0
            ? { mode: 'edit', id }
            : { mode: 'closed' },
    };
  }, [searchParams]);

  /**
   * Applies a change to the filters.
   *
   * `replace`, so typing in the search box does not bury the page the reader arrived
   * from under one history entry per keystroke.
   */
  const update = useCallback(
    (changes: Partial<Omit<UserParams, 'selection'>>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          for (const [key, value] of Object.entries(changes)) {
            // The field's name and its parameter's name are not the same — `query`
            // is `q` in the URL, because this query string is meant to be read.
            const param = KEYS[key as keyof typeof KEYS];

            if (value === '' || value === undefined || value === null) {
              next.delete(param);
            } else {
              next.set(param, String(value));
            }
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const select = useCallback(
    (selection: Selection) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          if (selection.mode === 'closed') next.delete(KEYS.id);
          else if (selection.mode === 'create') next.set(KEYS.id, CREATE);
          else next.set(KEYS.id, String(selection.id));

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** Whether anything is narrowing the list — what "Clear all" is for. */
  const hasFilters =
    params.query !== '' || params.role !== '' || params.state !== '' || params.access !== '';

  const reset = useCallback(
    () => update({ query: '', role: '', state: '', access: '' }),
    [update],
  );

  return { params, update, select, reset, hasFilters };
};
